import memoize from 'lodash/memoize'

import { createSelector } from '@app/utils/createSelector'
import { catchFetchDataError } from '@app/utils/performFetchData'

import { isApiError } from '@app/packages/api/errors'
import type { V3Promocode } from '@app/packages/api/v3/types/lib'
import { createLoadable } from '@app/packages/thunkSelector/thunkSelector'
import type { SliceState } from '@app/packages/thunkSelector/withCheckedSliceState'
import { withCheckedSliceState } from '@app/packages/thunkSelector/withCheckedSliceState'

import { callApi } from '@app/store/actions/api'
import { createReduxSlice } from '@app/store/redux_slice'

import { chosenRegionIdResultSelector } from './profile_place'

/**
 * What a code gives this visitor, checked without signing in; `null` when there is no such code. A
 * referral code's sitter discount is priced for the region they chose, and comes without an amount
 * until they have chosen one.
 */
export const promocodeCheckResultSelector = memoize((code: string) =>
  createSelector([promocodeCheckSlice.selector, chosenRegionIdResultSelector], (sliceState, regionIdResult) =>
    createLoadable(c => {
      const regionId = c.unwrap(regionIdResult)
      return withCheckedSliceState(c, {
        sliceState,
        slice: promocodeCheckSlice,
        hash: `${code}:${regionId ?? ''}`,
        load: async dispatch => {
          const { getPromocodesByCode } = await import('@app/packages/api/v3/actions/promocodes')
          try {
            return (await dispatch(callApi(getPromocodesByCode(code, { region_id: regionId ?? undefined })))).data
          } catch (e) {
            // A 404 is the answer, not a failure: that code does not exist.
            if (isApiError(e) && e.status === 404) return null
            return catchFetchDataError(dispatch, 'Promocode check failed')(e)
          }
        },
      })
    })
  )
)

const promocodeCheckSlice = createReduxSlice<SliceState<V3Promocode | null>>('promocode_check')
